import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { useSetUserSignInCredentials } from '@/api/api-mutations'
import { useGetUserSignInCredentials } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Input } from '@/components/base/input'
import { Label } from '@/components/base/label'
import { Switch } from '@/components/base/switch'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup } from '@/components/form/form'
import { RouteModal, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/users/$userUuid/edit-sign-in-credentials')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.Users.SignInCredentials.Title()
	}
})

function RouteComponent() {
	return (
		<RouteModal asDrawer>
			<EditSignInCredentialsForm />
		</RouteModal>
	)
}

function EditSignInCredentialsForm() {
	const { L } = useI18n()
	const { userUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()
	const credentials = useGetUserSignInCredentials({ userUuid })
	const setCredentials = useSetUserSignInCredentials()

	const [signInId, setSignInId] = useState('')
	const [pin, setPin] = useState('')
	const [clearPin, setClearPin] = useState(false)
	const [saving, setSaving] = useState(false)

	const hasPin = credentials.data?.hasPin ?? false

	// Populate local form state once the existing credentials have loaded.
	useEffect(() => {
		if (credentials.data) {
			setSignInId(credentials.data.signInId ?? '')
		}
	}, [credentials.data])

	const handleSave = async () => {
		// Don't submit until the existing credentials have loaded, otherwise the
		// empty initial signInId would be sent as null and clear an existing ID.
		if (credentials.isPending) {
			return
		}

		const trimmedSignInId = signInId.trim()

		// Validate client-side so we can show the correct error: the backend returns
		// 400 for both a non-numeric sign-in ID and an invalid PIN, which we can't
		// otherwise tell apart.
		if (trimmedSignInId !== '' && !/^\d+$/.test(trimmedSignInId)) {
			toast.error(L.Settings.Users.SignInCredentials.SignInIdNumeric())
			return
		}

		const body: { signInId?: string | null; pin?: string | null } = {
			signInId: trimmedSignInId === '' ? null : trimmedSignInId
		}

		if (clearPin) {
			body.pin = null
		} else if (pin !== '') {
			if (!/^\d{6}$/.test(pin)) {
				toast.error(L.Settings.Users.SignInCredentials.PinInvalid())
				return
			}
			body.pin = pin
		}

		setSaving(true)
		try {
			const result = await setCredentials({ path: { userUuid }, body })

			if (result.isSuccess) {
				toast.success(L.Settings.Users.SignInCredentials.SaveSuccess())
				handleSuccess()
				return
			}

			if (result.status === 409) {
				toast.error(L.Settings.Users.SignInCredentials.SignInIdTaken())
			} else if (result.status === 400) {
				toast.error(L.Settings.Users.SignInCredentials.PinInvalid())
			} else {
				toast.error(L.Error.SomethingWentWrong())
			}
		} finally {
			setSaving(false)
		}
	}

	return (
		<>
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.Users.SignInCredentials.Title()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<p className='txt-compact-small text-ui-fg-subtle'>
						{L.Settings.Users.SignInCredentials.Description()}
					</p>
					<FormFieldGroup>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='sign-in-id' size='small' weight='plus'>
								{L.Settings.Users.SignInCredentials.SignInId()}
							</Label>
							<Input
								id='sign-in-id'
								inputMode='numeric'
								value={signInId}
								onChange={e => setSignInId(e.target.value.replace(/\D/g, ''))}
								placeholder={L.Settings.Users.SignInCredentials.SignInIdPlaceholder()}
								disabled={credentials.isPending}
							/>
							<span className='txt-compact-xsmall text-ui-fg-muted'>
								{L.Settings.Users.SignInCredentials.SignInIdHint()}
							</span>
						</div>
					</FormFieldGroup>
					<FormFieldGroup>
						<div className='flex flex-col gap-1.5'>
							<Label htmlFor='sign-in-pin' size='small' weight='plus'>
								{L.Settings.Users.SignInCredentials.NewPin()}
							</Label>
							<Input
								id='sign-in-pin'
								inputMode='numeric'
								maxLength={6}
								autoComplete='off'
								value={pin}
								onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
								placeholder={L.Settings.Users.SignInCredentials.PinPlaceholder()}
								disabled={clearPin || credentials.isPending}
							/>
							<span className='txt-compact-xsmall text-ui-fg-muted'>
								{hasPin
									? L.Settings.Users.SignInCredentials.PinSet()
									: L.Settings.Users.SignInCredentials.PinNotSet()}
							</span>
						</div>
						{hasPin ? (
							<div className='flex items-center justify-between'>
								<Label htmlFor='sign-in-clear-pin' size='small'>
									{L.Settings.Users.SignInCredentials.ClearPin()}
								</Label>
								<Switch
									id='sign-in-clear-pin'
									checked={clearPin}
									onCheckedChange={value => {
										setClearPin(value)
										if (value) setPin('')
									}}
								/>
							</div>
						) : null}
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<Button onClick={() => void handleSave()} isLoading={saving} disabled={credentials.isPending}>
						{L.Settings.Users.SignInCredentials.Save()}
					</Button>
				</div>
			</Drawer.Footer>
		</>
	)
}
